/**
 * mySets — the sets a swimmer saved from a friend's link (deep-link N2).
 *
 * Storage: localStorage key `swimset.mySets.v1`, value
 *   { "v": 1, "sets": [{ "savedAt": ISO, "from": string | null, "card": ShareSetCard }] }
 * newest first. Scott's lean (2026-09-10): localStorage now; a native or
 * CloudKit store later. A later shape is a NEW key (`.v2`), so an older bundle
 * (the bundled fallback) can never overwrite a newer one's list.
 *
 * Idempotent by set signature: the same set saved twice is one entry. The
 * signature is the set itself (name + each block's reps, metres, stroke, role,
 * send-off), never the sender's numbers (pace, lineage, story, total) or
 * wording (labels), which change every time the sender swims it. The first
 * save wins; a repeat changes nothing.
 *
 * Untrusted on the way back in too: every stored card is re-checked with
 * `isShareCard` and the signature is recomputed, never read. An unreadable
 * value is treated as an empty list, and its raw text is copied to
 * `swimset.mySets.v1.corrupt` before the next save replaces it, so a bad write
 * never silently takes the good bytes with it.
 */
import { writable } from "svelte/store";
import { isShareCard, type ShareSet, type ShareSetCard } from "../shareCard/types";

export const MY_SETS_KEY = "swimset.mySets.v1";
export const MY_SETS_CORRUPT_KEY = `${MY_SETS_KEY}.corrupt`;

export interface MySet {
  /** Recomputed from the card on every read; not stored. */
  signature: string;
  savedAt: string;
  /** The card's owner, the friend who sent it. */
  from: string | null;
  card: ShareSetCard;
}

type StoredEntry = Omit<MySet, "signature">;

export type SaveResult =
  | { ok: true; entry: MySet; added: boolean }
  | { ok: false; reason: string };

const norm = (s: string) => s.trim().replace(/\s+/g, " ").toLowerCase();

export function setSignature(set: ShareSet): string {
  return JSON.stringify([
    norm(set.name),
    set.blocks.map((b) => [b.reps, b.distanceM, norm(b.stroke), b.role, b.intervalSec ?? null]),
  ]);
}

interface Loaded {
  sets: MySet[];
  /** The raw value, when there is one. */
  raw: string | null;
  /** True when `raw` held anything this reader had to drop. */
  lossy: boolean;
}

function load(): Loaded {
  let raw: string | null;
  try {
    raw = typeof localStorage === "undefined" ? null : localStorage.getItem(MY_SETS_KEY);
  } catch {
    return { sets: [], raw: null, lossy: false };
  }
  if (raw === null) return { sets: [], raw, lossy: false };
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { sets: [], raw, lossy: true };
  }
  const body = parsed as { v?: unknown; sets?: unknown } | null;
  if (!body || typeof body !== "object" || body.v !== 1 || !Array.isArray(body.sets)) {
    return { sets: [], raw, lossy: true };
  }
  const sets: MySet[] = [];
  const seen = new Set<string>();
  let lossy = false;
  for (const e of body.sets as unknown[]) {
    const entry = e as Partial<StoredEntry> | null;
    const card = entry?.card;
    if (
      !entry ||
      typeof entry.savedAt !== "string" ||
      !(entry.from === null || typeof entry.from === "string") ||
      !isShareCard(card) ||
      card.kind !== "set"
    ) {
      lossy = true;
      continue;
    }
    const signature = setSignature(card.set);
    if (seen.has(signature)) {
      lossy = true;
      continue;
    }
    seen.add(signature);
    sets.push({ signature, savedAt: entry.savedAt, from: entry.from, card });
  }
  return { sets, raw, lossy };
}

function write(sets: MySet[], previous: Loaded): void {
  if (previous.lossy && previous.raw !== null) localStorage.setItem(MY_SETS_CORRUPT_KEY, previous.raw);
  const stored: StoredEntry[] = sets.map(({ savedAt, from, card }) => ({ savedAt, from, card }));
  localStorage.setItem(MY_SETS_KEY, JSON.stringify({ v: 1, sets: stored }));
}

/** Newest first. Never throws; unreadable storage reads as an empty list. */
export function listMySets(): MySet[] {
  return load().sets;
}

/** The store the Scores tab renders. Refreshed by every save / remove here. */
export const mySets = writable<MySet[]>(listMySets());

/** Re-read after another web view (native tabs) may have written. */
export function refreshMySets(): void {
  mySets.set(listMySets());
}

export function saveMySet(card: ShareSetCard, now: Date = new Date()): SaveResult {
  const loaded = load();
  const signature = setSignature(card.set);
  const existing = loaded.sets.find((s) => s.signature === signature);
  if (existing) return { ok: true, entry: existing, added: false };
  const entry: MySet = { signature, savedAt: now.toISOString(), from: card.owner ?? null, card };
  const next = [entry, ...loaded.sets];
  try {
    write(next, loaded);
  } catch (err) {
    return { ok: false, reason: err instanceof Error ? err.message : String(err) };
  }
  mySets.set(next);
  return { ok: true, entry, added: true };
}

/** True when an entry with this signature was there and is gone. */
export function removeMySet(signature: string): boolean {
  const loaded = load();
  const next = loaded.sets.filter((s) => s.signature !== signature);
  if (next.length === loaded.sets.length) return false;
  try {
    write(next, loaded);
  } catch {
    return false;
  }
  mySets.set(next);
  return true;
}
